import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import "./EligibilityTip.css";

/* Reference-only guideline table. Mirrors the engine limits matrix
   (limits_all_*.csv, standard full-doc, 1-4 units, ex-TX). Final eligibility is
   still decided by the engine on submit. */

const toInt = (v) => {
  if (v == null) return NaN;
  const n = parseInt(String(v).replace(/[^\d]/g, ""), 10);
  return isNaN(n) ? NaN : n;
};

// Max CLTV by property type for a qualifying-FICO band. null = not eligible.
function bandLimits(fico) {
  if (fico < 640) return { primary: null, second: null, investor: null };
  if (fico < 660) return { primary: 65, second: 65, investor: null };
  if (fico < 680) return { primary: 75, second: 75, investor: 60 };
  if (fico < 700) return { primary: 80, second: 80, investor: 65 };
  if (fico < 720) return { primary: 85, second: 80, investor: 65 };
  if (fico < 740) return { primary: 85, second: 80, investor: 70 };
  return { primary: 85, second: 85, investor: 75 };
}

const cell = (v) =>
  v == null ? <span className="bf-elig-no">Not eligible</span> : `${v}%`;

export function EligibilityTip() {
  const ctx = useFormContext();
  const control = ctx ? ctx.control : undefined;
  // Watch only the two FICO fields — keep re-renders minimal.
  const watched = useWatch({
    control,
    name: [
      "borrower_credit_score_at_origination",
      "co_borrower_credit_score_at_origination",
    ],
  });
  const [bfico, cfico] = watched || [];
  const ficoVals = [toInt(bfico), toInt(cfico)].filter(
    (x) => !isNaN(x) && x >= 300 && x <= 850
  );
  if (!ficoVals.length) return null; // appears once a FICO is entered

  const fico = Math.min(...ficoVals); // qualifying FICO = lower of the two
  const lim = bandLimits(fico);
  const allIneligible =
    lim.primary == null && lim.second == null && lim.investor == null;

  return (
    <div className="bf-elig-tip">
      <div className="bf-elig-head">
        Guideline max CLTV at a <strong>{fico}</strong> qualifying score
      </div>
      {allIneligible ? (
        <p className="bf-elig-no-row">
          Below program minimum — most products need about <strong>640+</strong>{" "}
          (investment properties ~660+).
        </p>
      ) : (
        <table className="bf-elig-table">
          <tbody>
            <tr>
              <td>Primary residence</td>
              <td>{cell(lim.primary)}</td>
            </tr>
            <tr>
              <td>Second home</td>
              <td>{cell(lim.second)}</td>
            </tr>
            <tr>
              <td>Investment property</td>
              <td>{cell(lim.investor)}</td>
            </tr>
          </tbody>
        </table>
      )}
      <div className="bf-elig-note">
        Max DTI 50% · max loan $500,000. Reduced-documentation (bank-statement /
        alt-doc) loans need ~700 FICO and cap DTI at 43%. Final eligibility is set
        when you submit.
      </div>
    </div>
  );
}

export default EligibilityTip;
